<script lang="ts">
  import ArrowBackwardIosRounded from '@athena-lib/Icons/ArrowBackwardIosRounded.svelte';
  import ArrowForwardIosRounded from '@athena-lib/Icons/ArrowForwardIosRounded.svelte';
  import clsx from 'clsx';
  import type { Snippet } from 'svelte';
  const { children, class: className }: { children: Snippet; class?: string } = $props();

  let container: HTMLElement | undefined = $state();
  let containerWidth: number = $state(0);
  let containerScroll: number = $state(0);

  // We can't get scrollLeft from container, since it doesn't update.
  let scrollLeft: number = $state(0);

  let hideLeftButton = $derived(scrollLeft === 0);

  $effect(() => {
    containerWidth = container?.getBoundingClientRect().width ?? 0;
    containerScroll = container?.scrollWidth ?? 0;
  });

  $effect(() => {
    const onResize = () => {
      containerWidth = container?.getBoundingClientRect().width ?? 0;
      containerScroll = container?.scrollWidth ?? 0;
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  });

  let hideRightButton = $derived.by(() => {
    const total = scrollLeft + containerWidth;

    // if the scrollLeft + containerWidth is within 10 pixels of the containerScroll
    return total > containerScroll - 5 && total < containerScroll + 5;
  });

  const scrollOnePage =
    (direction: 'left' | 'right' = 'right') =>
    () => {
      if (!container) {
        return;
      }
      const width = container?.getBoundingClientRect().width;
      container?.scrollBy({
        left: (direction === 'left' ? -1 : 1) * width * 0.8,
        behavior: 'smooth',
      });
    };

  const maskClass = $derived.by(() => {
    const showLeftButton = !hideLeftButton;
    const showRightButton = !hideRightButton;

    if (showLeftButton && showRightButton) {
      return 'showBothButtons';
    }
    if (showLeftButton) {
      return 'showLeftButton';
    }
    if (showRightButton) {
      return 'showRightButton';
    }
    return undefined;
  });
</script>

<div class={clsx('relative ', className)}>
  {#each ['right', 'left'] as const as direction}
    <div
      class={clsx(
        'absolute bottom-0 top-0 z-10 content-center text-gray-500',
        direction === 'left' ? 'left-1' : 'right-0',
      )}
    >
      <button
        type="button"
        class={clsx(
          'block max-sm:hidden',
          direction === 'left' ? hideLeftButton && 'hidden' : '',
          direction === 'right' ? hideRightButton && 'hidden' : '',
        )}
        onclick={scrollOnePage(direction)}
      >
        {#if direction === 'right'}
          <ArrowForwardIosRounded sizeClass={clsx('w-6 h-full')} />
        {:else}
          <ArrowBackwardIosRounded sizeClass={clsx('w-6 h-full')} />
        {/if}
      </button>
    </div>
  {/each}
  <div
    bind:this={container}
    onscroll={(e) => {
      scrollLeft = e.currentTarget?.scrollLeft ?? 0;
    }}
    class={clsx(
      'flex flex-row overflow-x-scroll scroll-smooth scrollbar-none',
      className,
      maskClass,
    )}
  >
    {@render children()}
  </div>
</div>

<style>
  @media (min-width: 640px) {
    .showBothButtons {
      mask-image: linear-gradient(to left, transparent 0%, #fff 8% 92%, transparent 100%);
    }
    .showRightButton {
      mask-image: linear-gradient(to left, transparent 0%, #fff 8% 92%);
    }
    .showLeftButton {
      mask-image: linear-gradient(to left, #fff 8% 92%, transparent 100%);
    }
  }
</style>
